import {
  Control,
  FormState,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import AddressAccordion from '../../user-details/profile-information/address-accordion';
import EmailsAccordion from '../../user-details/profile-information/emails-accordion';
import GenderAccordion from '../../user-details/profile-information/gender-accordion';
import IdsAccordion from '../../user-details/profile-information/ids-accordion';
import ImsAccordion from '../../user-details/profile-information/ims-accordion';
import NotesAccordion from '../../user-details/profile-information/notes-accordion';
import OrganizationsAccordion from '../../user-details/profile-information/organizations-accordion';
import PhonesAccordion from '../../user-details/profile-information/phones-accordion';
import RelationsAccordion from '../../user-details/profile-information/relations-accordion';
import WebsitesAccordion from '../../user-details/profile-information/websites-accordion';
import { UserFormValues } from '../user-schema';

import Box from '@mui/material/Box';
import { FormSectionHeader } from '../../../../shared/form-section-header';
import Stack from '@mui/material/Stack';

interface StepProfileInformationProps {
  setValue: UseFormSetValue<UserFormValues>;
  getValues: UseFormGetValues<UserFormValues>;
  control: Control<UserFormValues>;
  formState: FormState<UserFormValues>;
  watch: UseFormWatch<UserFormValues>;
  directoryRoles: string[];
}

export default function StepProfileInformation({
  setValue,
  getValues,
  control,
  formState,
  watch,
  directoryRoles,
}: StepProfileInformationProps) {
  return (
    <Box sx={{ width: '100%' }}>
      <FormSectionHeader
        description="Add additional information about the user by clicking the plus icon on
        the accordion sections below. You can add multiple items to each by
        clicking the plus icon again and reorder as needed."
        sx={{ mb: 2 }}
      >
        Profile Information
      </FormSectionHeader>

      <Stack flexDirection="column" sx={{ p: 1, gap: 1 }}>
        <AddressAccordion
          control={control}
          errors={formState.errors}
          setValue={setValue}
          watch={watch}
          getValues={getValues}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditAddresses.value
            )
          }
        />
        <EmailsAccordion
          control={control}
          errors={formState.errors}
          watch={watch}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditEmailAddress.value
            )
          }
        />
        <GenderAccordion
          control={control}
          errors={formState.errors}
          watch={watch}
          user={null}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditGender.value
            )
          }
        />
        <IdsAccordion
          control={control}
          watch={watch}
          errors={formState.errors}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditExternalIds.value
            )
          }
        />
        <ImsAccordion
          control={control}
          watch={watch}
          errors={formState.errors}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditInstantMessaging.value
            )
          }
        />
        <NotesAccordion
          control={control}
          user={null}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditNotes.value
            )
          }
        />

        <OrganizationsAccordion
          control={control}
          watch={watch}
          errors={formState.errors}
          setValue={setValue}
          getValues={getValues}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditOrganizations.value
            )
          }
        />
        <PhonesAccordion
          control={control}
          watch={watch}
          errors={formState.errors}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditPhoneNumbers.value
            )
          }
        />
        <RelationsAccordion
          control={control}
          watch={watch}
          errors={formState.errors}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditRelationships.value
            )
          }
        />
        <WebsitesAccordion
          control={control}
          errors={formState.errors}
          watch={watch}
          disabled={
            !isAbleToSeeUIElement(
              directoryRoles,
              DirectoryRoles.EditWebsites.value
            )
          }
        />
      </Stack>
    </Box>
  );
}
